import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton, TextField } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import api from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal';
import { toast } from 'react-toastify';

const useStyles = makeStyles(theme => ({
  columnWrapper: props => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
    minWidth: 272,
    maxWidth: 272,
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
    borderRadius: 14,
    overflow: 'hidden',
    border: `1px solid ${theme.mode === 'light' ? 'rgba(15,61,46,0.08)' : 'rgba(255,255,255,0.08)'}`,
    boxShadow: theme.mode === 'light'
      ? '0 8px 24px rgba(15,61,46,0.08)'
      : '0 8px 24px rgba(0,0,0,0.30)',
    transition: 'box-shadow 0.18s ease, transform 0.18s ease',
    '&:hover': {
      boxShadow: theme.mode === 'light'
        ? '0 12px 30px rgba(15,61,46,0.12)'
        : '0 12px 30px rgba(0,0,0,0.38)',
    },
  }),
  columnTopBar: props => ({
    height: 4,
    backgroundColor: props.color || '#00875A',
    borderRadius: '12px 12px 0 0',
    flexShrink: 0,
  }),
  column: {
    backgroundColor: theme.mode === 'light' ? '#F5F7F6' : '#1E2230',
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    '@media (max-height: 850px)': {
      padding: theme.spacing(1),
    },
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
    '@media (max-height: 850px)': {
      marginBottom: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.5),
    },
  },
  columnTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
  },
  columnTitle: {
    fontWeight: 700,
    fontSize: '0.82rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.mode === 'light' ? '#42526E' : '#B8C0CC',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  countBadge: props => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: props.color || '#00875A',
    color: '#fff',
    borderRadius: 10,
    fontSize: '0.7rem',
    fontWeight: 700,
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    flexShrink: 0,
  }),
  columnMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  totalValue: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: theme.mode === 'light' ? '#00875A' : '#4ADE80',
    whiteSpace: 'nowrap',
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.mode === 'light' ? '#97A0AF' : '#5E6882',
    padding: 2,
    '&:hover': { color: theme.mode === 'light' ? '#42526E' : '#B8C0CC' },
  },
  editBtn: {
    padding: 2,
    color: theme.mode === 'light' ? '#97A0AF' : '#5E6882',
    '&:hover': { color: theme.mode === 'light' ? '#42526E' : '#B8C0CC' },
  },
  editRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
  },
  editInput: {
    '& .MuiInputBase-input': {
      fontSize: '0.78rem',
      fontWeight: 700,
      padding: '4px 6px',
    },
    flex: 1,
    minWidth: 0,
  },
  colorInput: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
  },
  iconBtn: {
    padding: 3,
  },
  cardList: props => ({
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    ...theme.scrollbarStylesSoft,
    maxHeight: 'none',
    paddingRight: 2,
  }),
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 60,
    color: theme.mode === 'light' ? '#C1C7D0' : '#3D4562',
    fontSize: '0.75rem',
    fontStyle: 'italic',
    borderRadius: 8,
    border: `2px dashed ${theme.mode === 'light' ? '#DFE1E6' : '#2A2D3E'}`,
    marginTop: theme.spacing(0.5),
  },
	loadMoreWrapper: {
		paddingTop: theme.spacing(1),
		textAlign: 'center',
		flexShrink: 0,
		'@media (max-height: 850px)': {
			paddingTop: theme.spacing(0.5),
		},
	},
	loadMoreButton: {
		width: '100%',
		border: `1px solid ${theme.mode === 'light' ? '#C7D1CC' : '#3D4562'}`,
		borderRadius: 8,
		background: theme.mode === 'light' ? '#FFFFFF' : '#25283A',
		color: theme.mode === 'light' ? '#00875A' : '#4ADE80',
		fontSize: '0.75rem',
		fontWeight: 700,
		padding: '7px 10px',
		cursor: 'pointer',
		'&:disabled': { opacity: 0.55, cursor: 'default' },
	},
	loadedCount: {
		fontSize: '0.68rem',
		color: theme.mode === 'light' ? '#6B778C' : '#8892A4',
		marginTop: 4,
		'@media (max-height: 850px)': {
			fontSize: '0.62rem',
			marginTop: 2,
		},
	},
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, isAdmin, isSystem, onLaneEdit, total, hasMore, loadingMore, onLoadMore, aggregateValue }) => {
  const classes = useStyles({ color, isSystem });
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(title);
  const [editColor, setEditColor] = useState(color || '#757575');
  const [saving, setSaving] = useState(false);

  const loadedBatchValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + opportunityValue;
  }, 0);

  // Usa o total agregado da coluna inteira vindo do backend (nao muda ao
  // clicar em Ver mais); se por algum motivo nao vier, cai no calculo do
  // lote carregado como antes, sem quebrar a tela.
  const totalValue = Number.isFinite(Number(aggregateValue)) ? Number(aggregateValue) : loadedBatchValue;

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDeleteColumn = async () => {
    try {
      await api.delete(`/tags/${id}`);
      toast.success('Coluna excluída com sucesso');
      if (onLaneEdit) onLaneEdit();
    } catch (err) {
      toast.error('Erro ao excluir coluna');
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const handleEditStart = (e) => {
    e.stopPropagation();
    setEditName(title);
    setEditColor(color || '#757575');
    setEditing(true);
  };

  const handleEditCancel = () => {
    setEditing(false);
  };

  const handleEditSave = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.put(`/tags/${id}`, { name: editName.trim(), color: editColor, kanban: 1 });
      toast.success('Coluna atualizada!');
      setEditing(false);
      if (onLaneEdit) onLaneEdit();
    } catch (err) {
      toast.error('Erro ao salvar coluna');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleEditSave();
    if (e.key === 'Escape') handleEditCancel();
  };

  const handleListScroll = event => {
    if (!hasMore || loadingMore || typeof onLoadMore !== 'function') return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 120) {
      onLoadMore();
    }
  };

  return (
    <>
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={classes.columnWrapper}
        >
          <div
            className={classes.columnTopBar}
            style={{ backgroundColor: editing ? editColor : (color || '#00875A') }}
          />
          <div className={classes.column}>
            <div className={classes.columnHeader}>
              {editing ? (
                <div className={classes.editRow}>
                  <input
                    type="color"
                    className={classes.colorInput}
                    value={editColor}
                    onChange={e => setEditColor(e.target.value)}
                    title="Cor da coluna"
                  />
                  <TextField
                    className={classes.editInput}
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    size="small"
                    variant="outlined"
                    autoFocus
                    disabled={saving}
                  />
                  <IconButton className={classes.iconBtn} onClick={handleEditSave} disabled={saving} size="small" title="Salvar">
                    <CheckIcon style={{ fontSize: 16, color: '#00875A' }} />
                  </IconButton>
                  <IconButton className={classes.iconBtn} onClick={handleEditCancel} disabled={saving} size="small" title="Cancelar">
                    <CloseIcon style={{ fontSize: 16, color: '#BF2600' }} />
                  </IconButton>
                </div>
              ) : (
                <>
                  <div className={classes.columnTitleRow}>
                    {isAdmin && (
                      <div {...provided.dragHandleProps} className={classes.dragHandle}>
                        <DragIndicatorIcon style={{ fontSize: 14 }} />
                      </div>
                    )}
                    <Typography className={classes.columnTitle} title={title}>
                      {title}
                    </Typography>
					<span className={classes.countBadge}>{Number.isFinite(Number(total)) ? Number(total) : tickets.length}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {totalValue > 0 && (
                      <div className={classes.columnMeta}>
                        <Typography className={classes.totalValue}>
                          R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </Typography>
                      </div>
                    )}
                    {isAdmin && !isSystem && (
                      <>
                        <IconButton className={classes.editBtn} onClick={handleEditStart} size="small" title="Editar coluna">
                          <EditIcon style={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Excluir coluna"
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirmOpen(true); }}
                          style={{ color: '#EF4444', padding: 2, marginLeft: 1 }}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 14 }} />
                        </IconButton>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <Droppable droppableId={id} type="CARD">
              {(provided, snapshot) => (
                <div
                  className={classes.cardList}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  onScroll={handleListScroll}
                  style={{
                    background: snapshot.isDraggingOver
                      ? 'rgba(0,135,90,0.04)'
                      : undefined,
                    borderRadius: 8,
                    transition: 'background 0.15s ease',
                  }}
                >
                  {tickets.map((ticket, index) => (
                    <KanbanCard
                      key={ticket.id}
                      ticket={ticket}
                      index={index}
                      updateTicket={updateTicket}
                      isSystem={!!isSystem}
                    />
                  ))}
                  {tickets.length === 0 && (
                    <div className={classes.emptyState}>vazio</div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
			{(hasMore || tickets.length > 0) && (
			  <div className={classes.loadMoreWrapper}>
				{hasMore && (
				  <button className={classes.loadMoreButton} onClick={() => onLoadMore && onLoadMore()} disabled={loadingMore} type="button">
					{loadingMore ? 'Carregando...' : 'Ver mais'}
				  </button>
				)}
				<div className={classes.loadedCount}>
				  {tickets.length} de {Number.isFinite(Number(total)) ? Number(total) : tickets.length} exibidos
				</div>
			  </div>
			)}
          </div>
        </div>
      )}

    </Draggable>
    <ConfirmationModal
      title="Excluir coluna"
      open={deleteConfirmOpen}
      onClose={() => setDeleteConfirmOpen(false)}
      onConfirm={handleDeleteColumn}
    >
      Tem certeza que deseja excluir a coluna <strong>{title}</strong>? Os tickets não serão apagados, apenas perderão essa etiqueta.
    </ConfirmationModal>
    </>
  );
};

export default KanbanColumn;
